import React, { useState, Fragment, useCallback } from "react";
import { Tab } from "@headlessui/react";
import { Menu } from "@headlessui/react";
import { useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import SubPageLayout from "../../components/shared/SubPageLayout";
import Modal from "../../components/shared/Modal";

// Drag and Drop Components
const DraggableElement = ({ id, type, content }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "canvasElement",
    item: { id, type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className={`p-4 border rounded-lg ${isDragging ? "opacity-50" : ""}`}
    >
      {content}
    </div>
  );
};

const DropZone = ({ onDrop, children, elements }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ["text", "image", "shape"],
    drop: (item) => onDrop(item),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <div
      ref={drop}
      className={`border-2 border-dashed border-gray-300 rounded-lg h-96 flex items-center justify-center relative ${
        isOver ? "bg-blue-50" : ""
      }`}
    >
      {children}
      {elements.map((element) => (
        <DraggableElement key={element.id} {...element} />
      ))}
    </div>
  );
};

const DraggableButton = ({ type, children }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type,
    item: { type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <button
      ref={drag}
      className={`px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-blue-700 ${
        isDragging ? "opacity-50" : ""
      }`}
    >
      {children}
    </button>
  );
};

const SocialMediaMarketing = () => {
  // State definitions
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTone, setSelectedTone] = useState("professional");
  const [showTooltips, setShowTooltips] = useState(true);
  const [canvasElements, setCanvasElements] = useState([]);
  const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // Event handlers
  const handleNewCampaign = () => {
    setShowNewCampaignModal(true);
  };

  const handleSchedulePost = () => {
    setShowScheduleModal(true);
  };

  const handleDrop = useCallback((item) => {
    setCanvasElements((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: item.type,
        content:
          item.type === "text"
            ? "New Text"
            : item.type === "image"
              ? "Image Placeholder"
              : "Shape",
      },
    ]);
  }, []);

  // Data
  const platforms = [
    { id: "facebook", name: "Facebook" },
    { id: "instagram", name: "Instagram" },
    { id: "twitter", name: "Twitter" },
    { id: "linkedin", name: "LinkedIn" },
    { id: "google", name: "Google" },
    { id: "youtube", name: "YouTube" },
  ];

  const toneOptions = [
    {
      id: "professional",
      name: "Professional",
      example: "Discover our latest innovation in...",
    },
    {
      id: "funny",
      name: "Funny/Witty",
      example: "Ready to level up? Our new product is here! 🚀",
    },
    {
      id: "inspirational",
      name: "Inspirational",
      example: "Transform your journey with...",
    },
    {
      id: "casual",
      name: "Casual",
      example: "Hey there! Check out what's new...",
    },
  ];

  const toolbarOptions = [
    { id: "font", name: "Font", icon: "text-size" },
    { id: "color", name: "Color", icon: "color-swatch" },
    { id: "align", name: "Align", icon: "align" },
    { id: "undo", name: "Undo", icon: "arrow-left" },
    { id: "redo", name: "Redo", icon: "arrow-right" },
  ];

  const tabPanels = [
    {
      name: "Content Creation",
      content: (
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-3 gap-6">
            {/* Canvas Editor */}
            <div className="col-span-2">
              {/* Template Selection Modal */}
              {showTemplateModal && (
                <Modal isOpen={showTemplateModal} onClose={() => setShowTemplateModal(false)} maxWidth="2xl">
                  <div className="p-6 bg-surface-default dark:bg-surface-default border-0">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        Choose a Template
                      </h3>
                      <button
                        onClick={() => setShowTemplateModal(false)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        "Instagram Post",
                        "Facebook Cover",
                        "LinkedIn Banner",
                      ].map((template) => (
                        <div
                          key={template}
                          className="border dark:border-gray-700 rounded-lg p-4 hover:border-[#202EC6] dark:hover:border-[#202EC6] cursor-pointer transition-colors bg-white dark:bg-surface-raised"
                        >
                          <div className="h-32 bg-gray-100 dark:bg-gray-800 rounded mb-2"></div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {template}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </Modal>
              )}

              {/* Editor Toolbar */}
              <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none mb-4">
                <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center space-x-4">
                  {toolbarOptions.map((tool) => (
                    <button
                      key={tool.id}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-gray-700 dark:text-gray-300 transition-colors"
                      title={tool.name}
                    >
                      <svg
                        className="w-5 h-5 text-gray-600 dark:text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                    </button>
                  ))}
                  <div className="border-l border-gray-200 dark:border-gray-700 pl-4">
                    <button
                      onClick={() => setShowTemplateModal(true)}
                      className="px-3 py-1 bg-[#202EC6] text-white rounded-md text-sm hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6] dark:focus:ring-offset-gray-900 transition-colors"
                    >
                      Templates
                    </button>
                  </div>
                </div>
              </div>

              {/* Canvas Area */}
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6">
                <DropZone onDrop={handleDrop} elements={canvasElements}>
                  {canvasElements.length === 0 && (
                    <p className="text-gray-600 dark:text-gray-400">Drag and drop elements here</p>
                  )}
                </DropZone>
                {showTooltips && (
                  <div className="absolute top-4 right-4 bg-blue-50 dark:bg-gray-800 text-blue-800 dark:text-gray-300 p-3 rounded-lg shadow-sm border border-blue-100 dark:border-gray-700">
                    <p className="text-sm">
                      👋 Welcome! Start by adding elements or choosing a template
                    </p>
                    <button
                      onClick={() => setShowTooltips(false)}
                      className="text-xs text-[#202EC6] hover:text-opacity-80 underline mt-1"
                    >
                      Got it
                    </button>
                  </div>
                )}
              </div>

              {/* Element Controls */}
              <div className="mt-4 flex justify-between">
                <div className="flex space-x-4">
                  <button className="px-4 py-2 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors">
                    Add Text
                  </button>
                  <button className="px-4 py-2 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors">
                    Add Image
                  </button>
                  <button className="px-4 py-2 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors">
                    Add Shape
                  </button>
                </div>
                <button className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
                  Show Layers
                </button>
              </div>
            </div>

            {/* AI Assistant */}
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-800">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  AI Assistant
                </h3>
                <div className="space-y-4">
                  {/* Tone Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select Tone
                    </label>
                    <select
                      value={selectedTone}
                      onChange={(e) => setSelectedTone(e.target.value)}
                      className="w-full rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-[#202EC6] focus:ring focus:ring-[#202EC6] focus:ring-opacity-50"
                    >
                      {toneOptions.map((tone) => (
                        <option
                          key={tone.id}
                          value={tone.id}
                          className="text-gray-900 dark:text-gray-100"
                        >
                          {tone.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      Example:{" "}
                      {toneOptions.find((t) => t.id === selectedTone)?.example}
                    </p>
                  </div>

                  {/* Content Generation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Generate Content
                    </label>
                    <textarea
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-[#202EC6] focus:ring focus:ring-[#202EC6] focus:ring-opacity-50"
                      rows="4"
                      placeholder="Describe what you want to create..."
                    />
                  </div>

                  <button className="w-full px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90 transition-colors">
                    Generate
                  </button>

                  {/* Platform Preview */}
                  <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Platform Preview
                    </h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-gray-900 dark:text-gray-100">Instagram</span>
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            2200 characters max
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Preview of your content on Instagram...
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-gray-900 dark:text-gray-100">Facebook</span>
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            63,206 characters max
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Preview of your content on Facebook...
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Best Practices */}
                  <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Best Practices
                    </h4>
                    <ul className="space-y-2">
                      <li className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                        <svg
                          className="w-4 h-4 text-green-500 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Add 2-3 relevant hashtags
                      </li>
                      <li className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                        <svg
                          className="w-4 h-4 text-green-500 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Include a call-to-action
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "Campaign Management",
      content: (
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-3 gap-6">
            {/* Active Campaigns */}
            <div className="col-span-2 bg-white dark:bg-gray-900 rounded-lg shadow-lg dark:shadow-none p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Active Campaigns
              </h3>
              <div className="space-y-4">
                {/* Campaign Cards */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md dark:shadow-none transition-shadow bg-white dark:bg-gray-800">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        Summer Promotion
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Running • 15 days left
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      Active
                    </span>
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                      <span>Budget spent: $1,200/$2,000</span>
                      <span>60%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1">
                      <div
                        className="bg-[#202EC6] h-2 rounded-full"
                        style={{ width: "60%" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Campaign Metrics */}
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg dark:shadow-none p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Campaign Metrics
              </h3>
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Engagement Rate
                  </h4>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">4.2%</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">+0.3% from last month</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-800">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Reach
                  </h4>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">12.5K</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">+2.1K from last month</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "Analytics",
      content: (
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-3 gap-6">
            {/* Analytics Dashboard */}
            <div className="col-span-2 bg-white dark:bg-gray-900 rounded-lg shadow-lg dark:shadow-none p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Performance Overview
              </h3>
              <div className="space-y-6">
                {/* Performance Metrics */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-800">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Total Reach
                    </h4>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">45.2K</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      ↑ 12% from last week
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-800">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Engagement Rate
                    </h4>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">3.8%</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      ↑ 0.5% from last week
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-800">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Conversion Rate
                    </h4>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">2.1%</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">-0.2% from last week</p>
                  </div>
                </div>

                {/* Top Performing Content */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Top Performing Content
                  </h4>
                  <div className="space-y-4">
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-gray-100">
                            Summer Collection Launch
                          </h5>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                            Posted on Instagram • 3 days ago
                          </p>
                        </div>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 text-xs rounded-full">
                          High Engagement
                        </span>
                      </div>
                      <div className="mt-3 flex space-x-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Likes
                          </p>
                          <p className="text-lg text-gray-700 dark:text-gray-300">1.2K</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Comments
                          </p>
                          <p className="text-lg text-gray-700 dark:text-gray-300">234</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Shares
                          </p>
                          <p className="text-lg text-gray-700 dark:text-gray-300">89</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Platform Breakdown */}
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg dark:shadow-none p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Platform Breakdown
              </h3>
              <div className="space-y-4">
                {platforms.slice(0, 4).map((platform) => (
                  <div key={platform.id} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {platform.name}
                      </h4>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        24% of total
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-2">
                      <div
                        className="bg-[#202EC6] h-2 rounded-full"
                        style={{ width: "24%" }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const breadcrumbItems = [
    { label: "Marketing & Sales", link: "/marketing-sales" },
    {
      label: "Social Media Marketing",
      link: "/marketing-sales/social-media-marketing",
    },
  ];

  return (
    <DndProvider backend={HTML5Backend}>
      <SubPageLayout
        title="Social Media Marketing"
        subtitle="Manage your social media presence and campaigns"
        breadcrumbItems={breadcrumbItems}
      >
        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none overflow-hidden">
          {/* Platform Selection */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleNewCampaign}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium bg-[#202EC6] text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg
                  className="h-5 w-5 mr-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                New Campaign
              </button>
              <button
                onClick={handleSchedulePost}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium bg-[#202EC6] text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg
                  className="h-5 w-5 mr-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Schedule Post
              </button>
            </div>

            {/* Platform Selection */}
            <div className="mt-4 flex flex-wrap gap-4">
              {platforms.map((platform) => (
                <button
                  key={platform.id}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    selectedPlatforms.includes(platform.id)
                      ? "bg-[#202EC6] text-white"
                      : "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-700"
                  }`}
                  onClick={() => {
                    setSelectedPlatforms((prev) =>
                      prev.includes(platform.id)
                        ? prev.filter((id) => id !== platform.id)
                        : [...prev, platform.id],
                    );
                  }}
                >
                  {platform.name}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <Tab.Group>
            <Tab.List className="px-6 pt-4 flex space-x-4 border-b border-gray-200 dark:border-gray-700">
              {tabPanels.map((tab, idx) => (
                <Tab key={idx} as={Fragment}>
                  {({ selected }) => (
                    <button
                      className={`pb-4 text-sm font-medium border-b-2 ${
                        selected
                          ? "text-[#202EC6] border-[#202EC6]"
                          : "text-gray-500 dark:text-gray-500 border-transparent hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
                      }`}
                    >
                      {tab.name}
                    </button>
                  )}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              {tabPanels.map((tab, idx) => (
                <Tab.Panel key={idx}>{tab.content}</Tab.Panel>
              ))}
            </Tab.Panels>
          </Tab.Group>
        </div>

        {/* New Campaign Modal */}
        <Modal 
          isOpen={showNewCampaignModal} 
          onClose={() => setShowNewCampaignModal(false)} 
          maxWidth="2xl"
        >
          <div className="p-6 bg-surface-default dark:bg-surface-default border-0">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Create New Campaign
              </h3>
              <button
                onClick={() => setShowNewCampaignModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Campaign Name
                </label>
                <input
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#202EC6] focus:ring focus:ring-[#202EC6] focus:ring-opacity-50 dark:focus:border-[#202EC6] dark:focus:ring-[#202EC6] bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="Enter campaign name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#202EC6] focus:ring focus:ring-[#202EC6] focus:ring-opacity-50 dark:focus:border-[#202EC6] dark:focus:ring-[#202EC6] bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  rows="3"
                  placeholder="Enter campaign description"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowNewCampaignModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button className="px-4 py-2 bg-[#202EC6] text-white rounded-md text-sm hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6] dark:focus:ring-offset-gray-900 transition-colors">
                  Create Campaign
                </button>
              </div>
            </div>
          </div>
        </Modal>

        {/* Schedule Post Modal */}
        <Modal 
          isOpen={showScheduleModal} 
          onClose={() => setShowScheduleModal(false)} 
          maxWidth="2xl"
        >
          <div className="p-6 bg-surface-default dark:bg-surface-default border-0">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Schedule Post
              </h3>
              <button
                onClick={() => setShowScheduleModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Date and Time
                </label>
                <input
                  type="datetime-local"
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#202EC6] focus:ring focus:ring-[#202EC6] focus:ring-opacity-50 dark:focus:border-[#202EC6] dark:focus:ring-[#202EC6] bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Platforms
                </label>
                <div className="mt-2 space-y-2">
                  {platforms.map((platform) => (
                    <label key={platform.id} className="flex items-center">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 dark:border-gray-600 text-[#202EC6] focus:ring-[#202EC6] bg-gray-50 dark:bg-gray-800"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {platform.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button className="px-4 py-2 bg-[#202EC6] text-white rounded-md text-sm hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6] dark:focus:ring-offset-gray-900 transition-colors">
                  Schedule
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </SubPageLayout>
    </DndProvider>
  );
};

export default SocialMediaMarketing;
